import React, { ReactNode, useRef } from 'react';
import classNames from 'classnames/bind';
import FocusLock from 'react-focus-lock';
import { Popup, Button, Icon, ICONS, Tooltip, TooltipPosition } from '@creditsights/styleguide-web';
import styles from './ContextMenu.module.scss';

const cx = classNames.bind(styles);

interface ContextMenuProps {
  isOpen: boolean;
  disabled?: boolean;
  setIsOpen: (boolean) => void;
  children: ReactNode;
  triggerIcon?: ReactNode;
  tooltip?: string;
  dataTestId?: string;
  bigger?: boolean;
  position?: TooltipPosition;
}

const ContextMenu = (props: ContextMenuProps): React.ReactElement => {
  const {
    children,
    triggerIcon,
    isOpen,
    setIsOpen,
    disabled,
    tooltip,
    dataTestId,
    bigger = false,
    position = TooltipPosition.BottomRight,
  } = props;
  const triggerRef = useRef<HTMLButtonElement>(null);

  const renderTriggerIcon = (): React.ReactElement => (
    <div
      data-testid="context-menu-trigger-icon"
      className={cx('trigger', { 'is-open': isOpen, 'is-disabled': disabled, bigger })}
    >
      {triggerIcon || <Icon name={ICONS.MenuDots} />}
    </div>
  );

  const renderTrigger = (): React.ReactElement => (
    <Tooltip
      trigger={
        <Button
          className={cx('body')}
          data-testid={dataTestId || 'context-menu-trigger'}
          onMouseUp={(e) => {
            e.stopPropagation();
          }}
          onClick={() => {
            setIsOpen(!isOpen);
          }}
          ref={triggerRef}
        >
          {renderTriggerIcon()}
        </Button>
      }
      content={tooltip}
      disabled={isOpen || !tooltip}
    />
  );

  return disabled ? (
    <div className={cx('body')}>{renderTriggerIcon()}</div>
  ) : (
    <Popup
      trigger={renderTrigger()}
      className={cx('dropdown', 'context-menu-container')}
      on="click"
      open={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      position={position}
      data-testid="actions-dropdown"
    >
      <FocusLock shards={[triggerRef]}>{children}</FocusLock>
    </Popup>
  );
};

export default ContextMenu;
